import { useState, useEffect } from 'react'
import { Box, Typography, Button, LinearProgress, Chip } from '@mui/material'
import { Warning, Timer, Upgrade } from '@mui/icons-material'
import { useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { tenantApi } from '../services/api'
import { RootState } from '../store'

interface QuotaGauge {
  resource: string
  current_usage: number
  max_allowed: number
  percentage_used: number
}

const TrialBanner = () => {
  const { user } = useSelector((state: RootState) => state.auth)
  const navigate = useNavigate()
  const [tenant, setTenant] = useState<any>(null)
  const [quotas, setQuotas] = useState<QuotaGauge[]>([])

  const tenantId = user?.tenant_id

  useEffect(() => {
    if (!tenantId) return
    const fetch = async () => {
      try {
        const [tRes, qRes] = await Promise.allSettled([
          tenantApi.getTenant(tenantId),
          tenantApi.getTenantQuotas(tenantId),
        ])
        if (tRes.status === 'fulfilled') setTenant(tRes.value.data)
        if (qRes.status === 'fulfilled') setQuotas(qRes.value.data || [])
      } catch {}
    }
    fetch()
  }, [tenantId])

  if (!tenant) return null

  const tier = tenant.tier
  const status = tenant.status

  // Only show for trial, grace_period, or expired tenants
  if (!['trial', 'free', 'grace_period', 'expired'].includes(tier) &&
      !['trial', 'grace_period', 'expired'].includes(status)) {
    return null
  }

  const trialEndsAt = tenant.trial_ends_at ? new Date(tenant.trial_ends_at) : null
  const graceEndsAt = tenant.grace_period_ends_at ? new Date(tenant.grace_period_ends_at) : null
  const now = new Date()

  let daysLeft: number | null = null
  let bannerColor: 'success' | 'warning' | 'error' | 'info' = 'info'
  let message = ''

  if (status === 'grace_period' && graceEndsAt) {
    daysLeft = Math.max(0, Math.ceil((graceEndsAt.getTime() - now.getTime()) / 86400000))
    bannerColor = 'warning'
    message = `Read-only mode — grace period ends in ${daysLeft} day${daysLeft !== 1 ? 's' : ''}. Upgrade to resume scanning.`
  } else if (status === 'expired') {
    bannerColor = 'error'
    message = 'Your trial has expired. Upgrade or request a grace period to view your data.'
  } else if (trialEndsAt) {
    daysLeft = Math.max(0, Math.ceil((trialEndsAt.getTime() - now.getTime()) / 86400000))
    if (daysLeft > 14) bannerColor = 'success'
    else if (daysLeft > 7) bannerColor = 'info'
    else if (daysLeft > 3) bannerColor = 'warning'
    else bannerColor = 'error'
    message = daysLeft > 0
      ? `Trial: ${daysLeft} day${daysLeft !== 1 ? 's' : ''} remaining`
      : 'Your trial has expired'
  } else if (status === 'trial') {
    bannerColor = 'info'
    message = 'Trial — run your first scan to start the 14-day clock'
  }

  if (!message) return null

  const colorMap = { success: '#e8f5e9', warning: '#fff3e0', error: '#fbe9e7', info: '#e3f2fd' }
  const textColorMap = { success: '#2e7d32', warning: '#e65100', error: '#c62828', info: '#1565c0' }

  return (
    <Box sx={{
      p: 1.5, mb: 2, borderRadius: 1,
      bgcolor: colorMap[bannerColor],
      border: `1px solid ${textColorMap[bannerColor]}30`,
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {bannerColor === 'error' ? <Warning sx={{ color: textColorMap[bannerColor] }} /> : <Timer sx={{ color: textColorMap[bannerColor] }} />}
          <Typography variant="body2" sx={{ color: textColorMap[bannerColor], fontWeight: 500 }}>
            {message}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {status === 'expired' && !tenant.grace_period_requested && (
            <Button
              size="small" variant="outlined"
              onClick={async () => {
                try {
                  await tenantApi.requestGracePeriod(tenantId!)
                  window.location.reload()
                } catch {}
              }}
            >
              Request Grace Period
            </Button>
          )}
          <Button
            size="small" variant="contained" startIcon={<Upgrade />}
            onClick={() => navigate('/pricing')}
          >
            Upgrade
          </Button>
        </Box>
      </Box>

      {quotas.length > 0 && (
        <Box sx={{ display: 'flex', gap: 2, mt: 1, flexWrap: 'wrap' }}>
          {quotas.filter(q => q.resource !== 'api_calls').map(q => (
            <Box key={q.resource} sx={{ minWidth: 120, flex: 1 }}>
              <Typography variant="caption" color="text.secondary">
                {q.resource === 'scans' ? 'Scans' : q.resource === 'users' ? 'Users' : q.resource}
                {' '}{q.current_usage}/{q.max_allowed}
              </Typography>
              <LinearProgress
                variant="determinate"
                value={Math.min(q.percentage_used, 100)}
                color={q.percentage_used >= 80 ? 'error' : q.percentage_used >= 60 ? 'warning' : 'primary'}
                sx={{ height: 6, borderRadius: 1 }}
              />
            </Box>
          ))}
        </Box>
      )}
    </Box>
  )
}

export default TrialBanner
